/**
 * Plan definitions — single source of truth for feature entitlements and pricing.
 * Imported by auth config (server), API middleware, and UI components.
 * @package @app/billing-config
 */

import type { PlanDisplay, PlanFeatures, PlanName } from './types';

// ---------------------------------------------------------------------------
// Feature entitlements per plan
// ---------------------------------------------------------------------------

export const PLAN_FEATURES: Record<PlanName, PlanFeatures> = {
  // --- Athletes ---
  free: {
    deals: 5,
    aiExtractions: 3,
    brandMatching: false,
    analytics: false,
    export: false,
    sms: false,
    athletes: 0,
    seats: 0,
    commissionTracking: false,
    recruitingDataset: true, // TEMP true for pilot testing (2026-08-12) — restore false for non-agent plans in PR 4
    whiteLabel: false,
    api: false,
    ferpa: false,
    sso: false,
  },
  pro: {
    deals: -1,
    aiExtractions: -1,
    brandMatching: true,
    analytics: true,
    export: true,
    sms: true,
    athletes: 0,
    seats: 0,
    commissionTracking: false,
    recruitingDataset: true, // TEMP true for pilot testing (2026-08-12) — restore false for non-agent plans in PR 4
    whiteLabel: false,
    api: false,
    ferpa: false,
    sso: false,
  },

  // --- Agents ---
  'agent-solo': {
    deals: -1,
    aiExtractions: -1,
    brandMatching: true,
    analytics: true,
    export: true,
    sms: true,
    athletes: 10,
    seats: 1,
    commissionTracking: true,
    recruitingDataset: true,
    whiteLabel: false,
    api: false,
    ferpa: false,
    sso: false,
  },
  'agent-team': {
    deals: -1,
    aiExtractions: -1,
    brandMatching: true,
    analytics: true,
    export: true,
    sms: true,
    athletes: 50,
    seats: 5,
    commissionTracking: true,
    recruitingDataset: true,
    whiteLabel: true,
    api: true,
    ferpa: false,
    sso: false,
  },
  'agent-enterprise': {
    deals: -1,
    aiExtractions: -1,
    brandMatching: true,
    analytics: true,
    export: true,
    sms: true,
    athletes: -1,
    seats: -1,
    commissionTracking: true,
    recruitingDataset: true,
    whiteLabel: true,
    api: true,
    ferpa: false,
    sso: true,
  },

  // --- Universities ---
  'university-starter': {
    deals: -1,
    aiExtractions: -1,
    brandMatching: true,
    analytics: true,
    export: true,
    sms: true,
    athletes: 100,
    seats: 2,
    commissionTracking: false,
    recruitingDataset: true, // TEMP true for pilot testing (2026-08-12) — restore false for non-agent plans in PR 4
    whiteLabel: false,
    api: false,
    ferpa: true,
    sso: false,
  },
  'university-standard': {
    deals: -1,
    aiExtractions: -1,
    brandMatching: true,
    analytics: true,
    export: true,
    sms: true,
    athletes: 300,
    seats: 5,
    commissionTracking: false,
    recruitingDataset: true, // TEMP true for pilot testing (2026-08-12) — restore false for non-agent plans in PR 4
    whiteLabel: false,
    api: false,
    ferpa: true,
    sso: true,
  },
  'university-enterprise': {
    deals: -1,
    aiExtractions: -1,
    brandMatching: true,
    analytics: true,
    export: true,
    sms: true,
    athletes: -1,
    seats: -1,
    commissionTracking: false,
    recruitingDataset: true, // TEMP true for pilot testing (2026-08-12) — restore false for non-agent plans in PR 4
    whiteLabel: false,
    api: true,
    ferpa: true,
    sso: true,
  },
};

// ---------------------------------------------------------------------------
// Monthly price per seat in USD (for MRR reporting)
// ---------------------------------------------------------------------------

export const PLAN_PRICES: Record<string, { monthly: number; annual?: number }> = {
  free: { monthly: 0 },
  pro: { monthly: 10, annual: 96 },
  'agent-solo': { monthly: 300 },
  'agent-team': { monthly: 500 },
  'university-starter': { monthly: 10 },
  'university-standard': { monthly: 7 },
  'university-enterprise': { monthly: 5 },
};

// ---------------------------------------------------------------------------
// Display metadata for pricing page
// ---------------------------------------------------------------------------

export const PLAN_DISPLAY: PlanDisplay[] = [
  // Athletes
  {
    name: 'free',
    displayName: 'Free',
    description: 'For athletes just getting started.',
    monthlyPrice: 0,
    annualPrice: null,
    segment: 'athlete',
    features: [
      'Up to 5 active deals',
      'Manual deal logging',
      'Payment & deliverable tracking',
      '3 AI screenshot extractions / month',
    ],
  },
  {
    name: 'pro',
    displayName: 'Pro',
    description: 'For athletes managing multiple deals.',
    monthlyPrice: 10,
    annualPrice: 96,
    segment: 'athlete',
    highlighted: true,
    features: [
      'Unlimited deals',
      'Unlimited AI extraction + contract summaries',
      'AI deal valuation',
      'Brand matching & advanced analytics',
      'Data export + SMS notifications',
    ],
  },
  // Agents
  {
    name: 'agent-solo',
    displayName: 'Solo',
    description: 'For independent agents managing up to 10 athletes.',
    monthlyPrice: 300,
    annualPrice: null,
    segment: 'agent',
    features: [
      'Multi-athlete dashboard',
      'Commission tracking & reconciliation',
      'Client reporting',
      'Full AI feature access',
    ],
  },
  {
    name: 'agent-team',
    displayName: 'Team',
    description: 'For small agencies — up to 50 athletes, 5 seats.',
    monthlyPrice: 500,
    annualPrice: null,
    segment: 'agent',
    features: [
      'Everything in Solo',
      'Team collaboration (5 seats)',
      'White-label reports',
      'API access, priority support',
    ],
  },
  {
    name: 'agent-enterprise',
    displayName: 'Enterprise',
    description: 'For 50+ athletes. Custom integrations & support.',
    monthlyPrice: null,
    annualPrice: null,
    segment: 'agent',
    features: [],
  },
  // Universities
  {
    name: 'university-starter',
    displayName: 'Starter',
    description: 'Up to 100 athletes.',
    monthlyPrice: 10,
    annualPrice: null,
    perUnit: '/athlete/mo',
    segment: 'university',
    features: [
      'FERPA-safe compliance dashboard',
      'Aggregate reporting + audit logs',
      '2 admin seats, email support',
      'Athletes get full Pro features',
    ],
  },
  {
    name: 'university-standard',
    displayName: 'Standard',
    description: '101–300 athletes.',
    monthlyPrice: 7,
    annualPrice: null,
    perUnit: '/athlete/mo',
    segment: 'university',
    features: [
      'Everything in Starter',
      'Custom branding + SSO',
      '5 admin seats',
      'Guided onboarding, chat support',
    ],
  },
  {
    name: 'university-enterprise',
    displayName: 'Enterprise',
    description: '301+ athletes.',
    monthlyPrice: 5,
    annualPrice: null,
    perUnit: '/athlete/mo',
    segment: 'university',
    features: [
      'Everything in Standard',
      'API + custom integrations',
      'Dedicated CSM, 99.9% SLA',
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Plan name → display label map.
 * Use `getPlanDisplayName()` for safe lookup with a sensible fallback.
 */
export const PLAN_LABELS: Record<PlanName, string> = {
  free: 'Free',
  pro: 'Pro',
  'agent-solo': 'Agent Solo',
  'agent-team': 'Agent Team',
  'agent-enterprise': 'Agent Enterprise',
  'university-starter': 'University Starter',
  'university-standard': 'University Standard',
  'university-enterprise': 'University Enterprise',
};

/**
 * Get the human-readable display name for a plan identifier.
 * Falls back to capitalizing the raw value for unknown plans.
 */
export function getPlanDisplayName(plan: string | null | undefined): string {
  if (!plan) return 'Free';
  if (plan in PLAN_LABELS) return PLAN_LABELS[plan as PlanName];
  // Fallback: capitalize first letter so we never render lowercase strings
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

/**
 * Check whether a feature is available for a given plan.
 * For numeric limits, returns true if the limit is > 0 or unlimited (-1).
 */
export function hasFeature(plan: PlanName, feature: keyof PlanFeatures): boolean {
  const features = PLAN_FEATURES[plan];
  if (!features) return false;
  const value = features[feature];
  if (typeof value === 'boolean') return value;
  return value === -1 || value > 0;
}

/**
 * Get the numeric limit for a feature (-1 = unlimited).
 * Returns 0 for boolean features that are false, 1 for true.
 */
export function getFeatureLimit(plan: PlanName, feature: keyof PlanFeatures): number {
  const features = PLAN_FEATURES[plan];
  if (!features) return 0;
  const value = features[feature];
  if (typeof value === 'boolean') return value ? 1 : 0;
  return value;
}
